import React, { useMemo } from 'react';
import { Avatar, SkeletonLoading } from '@saleshandy/design-system';
import { RotateClockwise, Trash } from '@saleshandy/icons';
import moment from 'moment-timezone';

import Table from '../../../shared/design-system/components/organisms/table';
import {
  Action,
  Column,
  PaginationShowHide,
} from '../../../shared/design-system/components/organisms/table/types';
import InframailIpsPopover from './inframail-ips-popover';

import { InframailData, InframailPaginationMeta } from '../types';
import { InframailIpAction, InframailIpStatus } from '../enums';
import { createRowSpanMap } from '../helpers/helper';

type InframailIpsTableProps = {
  inframailIps: InframailData[];
  inframailIpsMeta: InframailPaginationMeta;
  onAction: (key: string, row: InframailData) => void;
  onPaginationChange: (meta: Partial<InframailPaginationMeta>) => void;

  isLoading: boolean;
  onAddMoreDomainsClicked: (inframailIp: InframailData) => void;
  onAddMoreEmailAccountsClicked: (inframailIps: InframailData) => void;
};

const InframailIpsTable: React.FC<InframailIpsTableProps> = ({
  inframailIps,
  inframailIpsMeta,
  isLoading,
  onAction,
  onPaginationChange,

  onAddMoreDomainsClicked,
  onAddMoreEmailAccountsClicked,
}) => {
  const columns: Column[] = useMemo(
    () => [
      {
        dataField: 'ipAddress',
        text: 'Inframail Ips',
        component: (cell) => <span className="table-cell-text">{cell}</span>,
        headerLoadingSkeleton: <SkeletonLoading width={80} height={16} />,
        cellLoadingSkeleton: <SkeletonLoading width={120} height={20} />,
      },
      {
        dataField: 'totalDomains',
        text: 'Domains',
        component: (cell, row) => (
          <InframailIpsPopover
            cell={cell}
            row={row}
            type="domains"
            onAddMoreDomainsClicked={onAddMoreDomainsClicked}
            onAddMoreEmailAccountsClicked={onAddMoreEmailAccountsClicked}
          />
        ),
        headerLoadingSkeleton: <SkeletonLoading width={120} height={16} />,
        cellLoadingSkeleton: <SkeletonLoading width={120} height={20} />,
      },
      {
        dataField: 'totalMailboxes',
        text: 'Email Accounts',
        component: (cell, row) => (
          <InframailIpsPopover
            cell={cell}
            row={row}
            type="mailboxes"
            onAddMoreDomainsClicked={onAddMoreDomainsClicked}
            onAddMoreEmailAccountsClicked={onAddMoreEmailAccountsClicked}
          />
        ),
        headerLoadingSkeleton: <SkeletonLoading width={100} height={16} />,
        cellLoadingSkeleton: <SkeletonLoading width={120} height={20} />,
      },
      {
        dataField: 'createdAt',
        text: 'Created Date',
        component: (cell) => (
          <span className="table-cell-text">
            {moment(cell)?.format('DD MMM, YYYY') ?? '-'}
          </span>
        ),
        headerLoadingSkeleton: <SkeletonLoading width={100} height={16} />,
        cellLoadingSkeleton: <SkeletonLoading width={120} height={20} />,
      },
      {
        dataField: 'createdBy',
        text: 'Created by',
        component: (cell) => {
          const [firstName = '', lastName = ''] = (cell ?? '').split(' ');

          return (
            <div className="d-flex align-items-center gap-8">
              <Avatar firstName={firstName} lastName={lastName} />
              <span className="table-cell-text">{cell}</span>
            </div>
          );
        },
        headerLoadingSkeleton: <SkeletonLoading width={100} height={16} />,
        cellLoadingSkeleton: <SkeletonLoading width={120} height={20} />,
      },
    ],
    [],
  );

  const generateActionsColumn = (row: InframailData): Action[] => {
    if (row.status === InframailIpStatus.Deleted) {
      return [
        {
          key: InframailIpAction.REVOKE,
          displayName: 'Revoke Cancellation',
          iconElement: <RotateClockwise />,
          position: 'out',
        },
      ];
    }

    return [
      {
        key: InframailIpAction.DELETE,
        displayName: 'Delete',
        iconElement: <Trash />,
        position: 'out',
        disabled: row.status !== InframailIpStatus.Active,
      },
    ];
  };

  const revokeRowClasses = (row) => {
    if (row.status === InframailIpStatus.Deleted) {
      return 'disable-row';
    }

    return '';
  };

  const actionColumnRowSpanMapping = useMemo(
    () => createRowSpanMap(inframailIps),
    [inframailIps],
  );

  return (
    <div className="domains-table-container">
      <Table
        columns={columns}
        data={inframailIps}
        actionColumnName="Action"
        actions={(_c, row: InframailData) => generateActionsColumn(row)}
        onAction={onAction}
        onPaginationOptionsChange={onPaginationChange}
        paginationOptions={{
          options: {
            page: inframailIpsMeta.page,
            limit: inframailIpsMeta.pageSize,
            totalElements: inframailIpsMeta.total,
          },
        }}
        pagination={PaginationShowHide.SHOW}
        isNewPagination={true}
        headerVisibleForGenerateColumn={true}
        isLoading={isLoading}
        rowClasses={revokeRowClasses}
        actionColumnRowSpanMapping={actionColumnRowSpanMapping}
      />
    </div>
  );
};

export default InframailIpsTable;
